import { BoardMode, BoardPosition, coversPosition, generateModeLayout } from '@/data/board';
import { Difficulty } from '@/data/settings';
import { Card } from '@/types/game';
import { BOARD_PATTERNS, normalizePatternIndex } from '@/data/boardPatterns';

const routes = new Map<string, number[]>();
export const chinaDateKey = (now = Date.now()) => new Date(now + 8 * 3600000).toISOString().slice(0, 10);
export const modeSeed = (mode: BoardMode, day: string, stage: number, difficulty: Difficulty): number => {
  let seed = 2166136261;
  for (const char of mode + '|' + day + '|' + stage + '|' + difficulty + '|distributed-2') seed = Math.imul(seed ^ char.charCodeAt(0), 16777619);
  return seed >>> 0;
};
export const getModeWitness = (cards: Card[]): string[] => {
  const key = cards[0]?.id.split('-')[0];
  return (routes.get(key) || []).map(index => cards[index].id);
};
const revealOrder = (positions: BoardPosition[], rng: () => number, mode: BoardMode) => {
  const blockers = positions.map(() => 0), releases: number[][] = positions.map(() => []);
  positions.forEach((card, index) => positions.forEach((other, j) => {
    if (coversPosition(card, other)) {
      blockers[index]++; releases[j].push(index);
    }
  }));
  const available = blockers.map((count, index) => count === 0 ? index : -1).filter(index => index >= 0);
  const order: number[] = [], clearAt: number[] = []; let frontier: number[] = [];
  while (available.length) {
    let pool = available;
    if (mode === 'daily') {
      if (!frontier.length && available.length >= 6) {
        const sample = [...available];
        for (let i = sample.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [sample[i], sample[j]] = [sample[j], sample[i]]; }
        // Narrow original castles may reveal fewer than six cards between waves.
        // Include the unfinished prefix when choosing a boundary divisible by three.
        const prefix = order.length % 3;
        frontier = sample.slice(0, Math.floor((Math.min(24, sample.length) + prefix) / 3) * 3 - prefix);
        clearAt.push(order.length + frontier.length);
      }
      if (frontier.length) pool = frontier;
    }
    const last = positions[order[order.length - 1]];
    const spread = last ? pool.filter(i => Math.abs(positions[i].x - last.x) + Math.abs(positions[i].y - last.y) > 140) : pool;
    const candidates = spread.length ? spread : pool;
    const chosen = candidates[Math.floor(rng() * candidates.length)];
    frontier = frontier.filter(i => i !== chosen);
    available.splice(available.indexOf(chosen), 1); order.push(chosen);
    releases[chosen].forEach(next => { if (--blockers[next] === 0) available.push(next); });
  }
  if (order.length !== positions.length) throw new Error('Invalid board dependency graph');
  return { order, clearAt };
};

/** Windows of three in actual reveal piles / adjacent rows, not merely array order. */
export const cardChains = (positions: (Pick<BoardPosition, 'x' | 'y' | 'zIndex'> & Partial<Pick<BoardPosition, 'layer' | 'zone'>>)[]): number[][] => {
  const piles = new Map<string, number[]>(), rows = new Map<string, number[]>();
  positions.forEach((p, i) => {
    const key = p.zone?.startsWith('reserve') ? p.zone : p.zone === 'auxiliary' ? 'aux-' + p.x : 'main-' + p.x + ',' + p.y;
    const pile = piles.get(key) || []; pile.push(i); piles.set(key, pile);
    if (p.zone === 'main') { const rowKey = p.layer + ':' + p.y, row = rows.get(rowKey) || []; row.push(i); rows.set(rowKey, row); }
  });
  const chains = [...piles.values()].map(pile => pile.sort((a, b) => positions[b].zIndex - positions[a].zIndex));
  for (const row of rows.values()) {
    row.sort((a, b) => positions[a].x - positions[b].x);
    let strip: number[] = [];
    for (const i of row) {
      if (strip.length && positions[i].x - positions[strip[strip.length - 1]].x > 100) { chains.push(strip); strip = []; }
      strip.push(i);
    }
    chains.push(strip);
  }
  return chains;
};

/** Global balanced assignment on a mixed legal route; no prepacked triple bags. */
export const buildModeCards = (
  mode: BoardMode, types: string[], boardId: string, rng: () => number, difficulty: Difficulty, pattern: number,
  legacy?: { positions: BoardPosition[]; typeCounts: [string, number][] }
): Card[] => {
  const positions = legacy?.positions || generateModeLayout(mode, pattern);
  const groups = BOARD_PATTERNS[normalizePatternIndex(pattern)].groups;
  if (!legacy && types.length < Math.max(...groups.map(([type]) => type))) throw new Error('Missing source farm tile types');
  const typeCounts: [string, number][] = legacy?.typeCounts || groups.map(([type, count]) => [types[type - 1], count * 3]);
  if (typeCounts.reduce((sum, [, count]) => sum + count, 0) !== positions.length || typeCounts.some(([, count]) => count <= 0 || count % 3)) throw new Error('Invalid mode tile counts');
  const selected = typeCounts.map(([type]) => type);
  const triples: [number, number][][] = positions.map(() => []);
  for (const chain of cardChains(positions)) for (let i = 0; i + 2 < chain.length; i++) {
    const window = chain.slice(i, i + 3);
    window.forEach(index => triples[index].push(window.filter(j => j !== index) as [number, number]));
  }
  const limit = mode === 'daily' ? { easy: 2, normal: 3, hard: 4 }[difficulty] : difficulty === 'easy' ? 4 : 5;
  const capacity = mode === 'daily' ? { easy: 4, normal: 5, hard: 6 }[difficulty] : 6;
  for (let attempt = 0; attempt < 16; attempt++) {
    const { order: route, clearAt } = revealOrder(positions, rng, mode), remaining = new Map(typeCounts);
    const held = new Map<string, number>();
    const cards = positions.map(p => ({ ...p, id: boardId + '-' + p.zIndex, type: '', emoji: '', status: 0, isCover: false }));
    const sequence: string[] = [], choices: string[][] = []; let occupied = 0, cursor = 0, steps = 0;
    while (cursor < route.length && steps++ < 20000) {
      const index = route[cursor];
      if (!choices[cursor]) {
        const candidates = selected.filter(type => {
        const count = held.get(type) || 0;
        const boundary = clearAt.find(end => end > cursor);
        const needed = [...held.values()].reduce((sum, value) => sum + 3 - value, 0) + (count === 0 ? 2 : -1);
        return remaining.get(type)! > 0 && (count === 2 || (occupied < capacity && (count > 0 || held.size < limit)))
          && (boundary === undefined || needed <= boundary - cursor - 1)
          && !(sequence.length > 1 && sequence[sequence.length - 1] === type && sequence[sequence.length - 2] === type)
          && !triples[index].some(([a, b]) => cards[a].type === type && cards[b].type === type);
        });
        const ordered: string[] = [];
        while (candidates.length) {
          const weights = candidates.map(type => Math.pow(remaining.get(type)!, 2) *
            (type === sequence[sequence.length - 1] ? .2 : 1) * ((held.get(type) || 0) === 2 && occupied >= capacity - 1 ? 2 : 1));
          let roll = rng() * weights.reduce((sum, weight) => sum + weight, 0), chosen = candidates.length - 1;
          for (let i = 0; i < weights.length; i++) { roll -= weights[i]; if (roll <= 0) { chosen = i; break; } }
          ordered.push(candidates.splice(chosen, 1)[0]);
        }
        choices[cursor] = ordered.reverse();
      }
      if (!choices[cursor].length) {
        choices.length = cursor;
        if (cursor === 0) break;
        cursor--; const previous = sequence.pop()!, count = held.get(previous) || 0;
        remaining.set(previous, remaining.get(previous)! + 1);
        if (count === 0) { held.set(previous, 2); occupied += 2; }
        else { if (count === 1) held.delete(previous); else held.set(previous, count - 1); occupied--; }
        cards[route[cursor]].type = ''; cards[route[cursor]].emoji = ''; continue;
      }
      const type = choices[cursor].pop()!; cards[index].type = type; cards[index].emoji = type; sequence.push(type);
      remaining.set(type, remaining.get(type)! - 1);
      const count = (held.get(type) || 0) + 1;
      if (count === 3) { held.delete(type); occupied -= 2; } else { held.set(type, count); occupied++; }
      cursor++; choices.length = cursor;
    }
    if (sequence.length === cards.length) {
      routes.set(boardId, route); if (routes.size > 24) routes.delete(routes.keys().next().value!);
      return cards;
    }
  }
  throw new Error('Could not generate a solvable distributed board');
};
